import React, { useMemo } from 'react';
import { translate } from 'components/Translation';
import { exportToXLS } from 'utils/export/export';
import moment from 'moment';
import styles from 'assets/jss/material-dashboard-pro-react/components/csvSummaryDialogStyle';
import { makeStyles } from '@material-ui/core';
import CustomConfirmationDialog from 'components/CustomConfirmationDialog/CustomConfirmationDialog';
import ErrorMapper from 'utils/api/errors/errorMapper';
import { BodyM, Caption, DisplayM } from 'components/Typography/Typography';
import { ImportedUsersData } from 'utils/api/models/users/importedUsersData';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ICsvSummaryDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	summary: ImportedUsersData | null;
}

const CsvSummaryDialog = ({ isOpen, hideDialog, summary }: ICsvSummaryDialogProps) => {
	const classes = useStyles();

	const numberOfImportedUsers: number = useMemo(() => {
		if (summary && summary.importErrors) {
			return summary.rowsProcessed - summary.importErrors.length;
		}
		if (summary) {
			return summary.rowsProcessed;
		}
		return 0;
	}, [summary]);

	const showErrors: boolean = !!summary && summary.importErrors && summary.importErrors.length > 0;

	const exportXLS = (): void => {
		const data = summary?.importErrors.map((error) => ({
			'Row number': error.rowNumber,
			'Error type': ErrorMapper.mapToMessage(error.errorCode),
		}));
		exportToXLS(data, `csv import errors ${moment().format('LLL')}}`);
	};

	return (
		<CustomConfirmationDialog
			isOpen={isOpen}
			hideDialog={hideDialog}
			isError={numberOfImportedUsers === 0}
			button={
				showErrors
					? {
							color: 'tertiary-light',
							onClick: exportXLS,
							name: translate('users.download.errors.list'),
					  }
					: undefined
			}
			header={
				<>
					<BodyM>
						{numberOfImportedUsers > 0
							? translate('users.you.have.imported.new.users')
							: translate('users.something.went.wrong')}
					</BodyM>
					<DisplayM>{String(numberOfImportedUsers)}</DisplayM>
				</>
			}
		>
			<ConditionalRender
				show={!!summary}
				onTrue={() => (
					<div className={classes.bodyWrapper}>
						<ConditionalRender
							show={showErrors}
							onTrue={() => (
								<div className={classes.errors}>
									<div>
										<div className={classes.errorsHeader}>
											<Caption color="danger">
												{translate(
													'users.some.errors.occurred',
													String(summary?.importErrors.length)
												)}
											</Caption>
										</div>
										<div>
											{summary?.importErrors.map((error, index) => (
												<div key={index}>
													<Caption color="disabled-light" disableMargin>
														{translate('users.row.number', error.rowNumber)}
													</Caption>
													<Caption color="disabled-light" disableMargin>
														{translate(
															'users.error.type',
															ErrorMapper.mapToMessage(error.errorCode)
														)}
													</Caption>
												</div>
											))}
										</div>
									</div>
								</div>
							)}
						/>
					</div>
				)}
			/>
		</CustomConfirmationDialog>
	);
};

export default CsvSummaryDialog;
